import { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pause,
  Play,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminApi } from "@/lib/admin-api";
import { fmtDate, fmtRelative, useAdminList } from "./_useAdminList";

const STATUS_VARIANT = {
  completed: "default",
  succeeded: "default",
  running: "secondary",
  pending: "secondary",
  queued: "secondary",
  failed: "destructive",
  errored: "destructive",
};

export default function AdminJobs() {
  const [storeId, setStoreId] = useState("");
  const [eventName, setEventName] = useState("");
  const [status, setStatus] = useState("any");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const list = useAdminList({
    fetcher: adminApi.jobs,
    dataKey: "jobs",
    defaultPageSize: 50,
  });

  const totalPages = Math.max(1, Math.ceil(list.total / list.pageSize));

  const apply = () =>
    list.setFilters({
      storeId: storeId ? parseInt(storeId, 10) : undefined,
      eventName: eventName || undefined,
      status: status === "any" ? undefined : status,
    });

  // Lightweight 5s auto-refresh when toggled on. Staff-only page, so we don't
  // reach for a heavy polling lib. `list.refetch` is stable while filters/page
  // don't change, so the interval doesn't get torn down on every data tick.
  const { refetch } = list;
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => refetch(), 5000);
    return () => clearInterval(id);
  }, [autoRefresh, refetch]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Background jobs</h1>
          <p className="text-sm text-muted-foreground">
            {list.total.toLocaleString()} matching the current filter. Inngest fan-outs are recorded here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
          >
            {autoRefresh ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Auto-refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${list.refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <Input
          type="number"
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          placeholder="Store ID"
          className="h-9"
        />
        <Input
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") apply();
          }}
          placeholder="event name (e.g. competitor/sync.requested)"
          className="h-9 sm:col-span-2"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any status</SelectItem>
            <SelectItem value="pending">pending</SelectItem>
            <SelectItem value="queued">queued</SelectItem>
            <SelectItem value="running">running</SelectItem>
            <SelectItem value="completed">completed</SelectItem>
            <SelectItem value="failed">failed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={apply}>Apply</Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setStoreId("");
            setEventName("");
            setStatus("any");
            list.setFilters({ storeId: undefined, eventName: undefined, status: undefined });
          }}
        >
          Clear
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {list.loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading jobs…
            </div>
          ) : list.error ? (
            <div className="p-6 text-sm text-destructive">{list.error}</div>
          ) : list.data.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No jobs match.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Type / event</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Progress</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((j) => (
                  <TableRow key={j.id}>
                    <TableCell className="font-mono text-xs">{j.id}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-medium">{j.jobType || "—"}</div>
                      <div className="text-muted-foreground text-[11px] truncate max-w-[260px]">
                        {j.eventName}
                      </div>
                      {j.inngestJobId && (
                        <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[260px]">
                          {j.inngestJobId}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {j.store ? (
                        <>
                          <span className="font-mono">{j.store.id}</span>
                          <div className="text-muted-foreground truncate max-w-[140px]">
                            {j.store.shopDomain}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={STATUS_VARIANT[j.status] || "outline"}
                        className="text-xs"
                      >
                        {j.status || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {j.progress != null ? `${j.progress}%` : "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>{fmtRelative(j.startedAt || j.createdAt)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {fmtDate(j.startedAt || j.createdAt)}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{fmtRelative(j.updatedAt)}</TableCell>
                    <TableCell className="text-xs max-w-[280px]">
                      {j.errorMessage ? (
                        <span className="text-destructive line-clamp-2" title={j.errorMessage}>
                          {j.errorMessage}
                        </span>
                      ) : (
                        <span className="text-muted-foreground line-clamp-2">
                          {j.message || j.summary || "—"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div>
          Page {list.page} of {totalPages} · {list.total.toLocaleString()} jobs
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={list.page <= 1}
            onClick={() => list.setPage(list.page - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={list.page >= totalPages}
            onClick={() => list.setPage(list.page + 1)}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
